import React, { useState } from 'react';
import { Star, Heart } from 'lucide-react';
import type { FormField, RatingConfig } from '../../../types/forms';

interface RatingFieldProps {
  field: FormField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const EMOJI_FACES = ['😞', '😕', '😐', '🙂', '😍'];

export const RatingField: React.FC<RatingFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
}) => {
  const config = field.config as RatingConfig | undefined;
  const maxRating = config?.maxRating ?? 5;
  const iconType = config?.iconType ?? 'star';
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const currentValue = value ? parseInt(value, 10) : 0;
  const items = Array.from({ length: maxRating }, (_, i) => i + 1);

  const handleClick = (rating: number) => {
    if (disabled) return;
    onChange?.(String(rating));
  };

  if (iconType === 'emoji') {
    return (
      <div className="flex flex-wrap gap-3 justify-center" role="radiogroup" aria-label={field.label}>
        {items.map((rating) => {
          const emojiIndex = Math.round(((rating - 1) / (maxRating - 1)) * (EMOJI_FACES.length - 1));
          const isSelected = currentValue === rating;
          return (
            <button
              key={rating}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${rating} de ${maxRating} - ${EMOJI_FACES[emojiIndex]}`}
              onClick={() => handleClick(rating)}
              disabled={disabled}
              className={`
                text-2xl p-2 rounded-xl transition-all duration-200
                ${isSelected ? 'bg-primary-500/20 scale-125' : 'hover:scale-110 opacity-50 hover:opacity-100'}
                disabled:cursor-not-allowed
              `}
            >
              {EMOJI_FACES[emojiIndex]}
            </button>
          );
        })}
      </div>
    );
  }

  const IconComponent = iconType === 'heart' ? Heart : Star;

  return (
    <div
      className="flex flex-wrap gap-1 justify-center"
      role="radiogroup"
      aria-label={field.label}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {items.map((rating) => {
        const isFilled = rating <= (hoveredIndex ?? currentValue);
        return (
          <button
            key={rating}
            type="button"
            role="radio"
            aria-checked={currentValue === rating}
            aria-label={`${rating} de ${maxRating} ${iconType === 'heart' ? 'corações' : 'estrelas'}`}
            onClick={() => handleClick(rating)}
            onMouseEnter={() => !disabled && setHoveredIndex(rating)}
            disabled={disabled}
            className={`
              flex items-center justify-center min-w-11 min-h-11
              transition-all duration-200 disabled:cursor-not-allowed
              ${isFilled ? 'text-primary-500 scale-110' : 'text-neutral-600 hover:text-neutral-400'}
            `}
          >
            <IconComponent
              className={maxRating > 7 ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-8 h-8'}
              fill={isFilled ? 'currentColor' : 'none'}
            />
          </button>
        );
      })}
    </div>
  );
};
